import React from 'react';
import { PDFDownloadLink, Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

// =============== PDF STYLES ===============
const pdfStyles = StyleSheet.create({
    page: { padding: 20, fontSize: 9, fontFamily: 'Helvetica' },
    header: { backgroundColor: '#1a472a', padding: 10, marginBottom: 15, textAlign: 'center' },
    headerTitle: { color: 'white', fontSize: 16, fontWeight: 'bold' },
    headerSubtitle: { color: '#e0e0e0', fontSize: 11 },
    headerDate: { color: '#e0e0e0', fontSize: 8, marginTop: 4 },
    table: { width: '100%', marginBottom: 15, borderWidth: 1, borderColor: '#cccccc' },
    tableHeader: { backgroundColor: '#e8f5e9', flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#2e7d32', paddingVertical: 5 },
    tableRow: { flexDirection: 'row', borderBottomWidth: 0.5, borderBottomColor: '#dddddd', paddingVertical: 4, minHeight: 25 },
    colSno: { width: '6%', paddingLeft: 4, fontWeight: 'bold' },
    colParticulars: { width: '44%', paddingLeft: 4 },
    colStatus: { width: '25%', paddingLeft: 4 },
    colRemarks: { width: '25%', paddingLeft: 4 },
    sectionTitle: { fontSize: 12, fontWeight: 'bold', backgroundColor: '#f0f0f0', padding: 6, borderWidth: 1, borderColor: '#cccccc', marginTop: 10, marginBottom: 0 },
    footer: { marginTop: 20, textAlign: 'center', fontSize: 8, color: '#666666', borderTopWidth: 1, borderTopColor: '#cccccc', paddingTop: 8 },
    statusSuccess: { color: '#2e7d32', fontWeight: 'bold' },
    statusError: { color: '#d32f2f', fontWeight: 'bold' },
});

// FIXED: Proper formatYesNo function that returns the value correctly
const formatYesNo = (val) => {
    if (val === undefined || val === null || val === '') return '—';
    if (val === 'Yes' || val === 'YES' || val === true) return 'Yes';
    if (val === 'No' || val === 'NO' || val === false) return 'No';
    return val;
};

// Function to get status with styling (for PDF)
const getStatusWithStyle = (status, isYesNo) => {
    if (!isYesNo) return status || '—';
    const formatted = formatYesNo(status);
    return formatted;
};

// =============== PDF DOCUMENT (COMPLETE with ALL Sections) ===============
export const PDFReport = ({ data, data2, rowData, defData, acc, getSurvey, shiftAddr }) => {
    const formData = data2?.[0] || {};
    const apiExtra = data || {};
    const collegeData = apiExtra.data?.[0] || rowData || {};
    const deficiencies = Array.isArray(defData) ? defData : (apiExtra.def || []);
    const accomidation = Array.isArray(acc) ? acc : (apiExtra.accom || []);

    // SAFE extraction with null checks for insert_by and insert_time
    const firstDeficiency = deficiencies[0] || {};
    const insertBy = firstDeficiency.insert_by ||
        firstDeficiency.inserted_by ||
        formData.insert_by ||
        formData.inserted_by ||
        collegeData.insert_by ||
        'N/A';
    const formatDateToDMY = (dateValue) => {
        if (!dateValue) return 'N/A';
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return 'N/A';
        return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
    };
    const insertTime = formatDateToDMY(firstDeficiency.insert_time);
    firstDeficiency.inserted_time ||
        firstDeficiency.inserted_date ||
        firstDeficiency.created_at ||
        formData.insert_time ||
        formData.inserted_time ||
        formData.created_at ||
        new Date().toLocaleString();

    const ugCourses = Array.isArray(apiExtra.ugCourses) ? apiExtra.ugCourses : [];
    const pgCourses = Array.isArray(apiExtra.pgCourses) ? apiExtra.pgCourses : [];
    const corpusInstall = Array.isArray(apiExtra.corpusFundInstall) ? apiExtra.corpusFundInstall : [];
    const allCorpus = Array.isArray(apiExtra.corpusfundUGPG) ? apiExtra.corpusfundUGPG : [];

    const ugCorpusFunds = Array.isArray(allCorpus) ? allCorpus.filter(i => i.coll_type?.replace(/"/g, '').trim() === 'UG') : [];
    const pgBeforeCorpus = Array.isArray(allCorpus) ? allCorpus.filter(i => i.coll_type?.replace(/"/g, '').trim() === 'PG before') : [];
    const pgAfterCorpus = Array.isArray(allCorpus) ? allCorpus.filter(i => i.coll_type?.replace(/"/g, '').trim() === 'PG after') : [];

    // FIXED: TableRow component with proper status display
    const TableRow = ({ sno, particulars, status, remarks, isYesNo = false }) => {
        const displayStatus = isYesNo ? formatYesNo(status) : (status || '—');
        const isStatusYes = isYesNo && status === 'Yes';
        const isStatusNo = isYesNo && status === 'No';

        return (
            <View style={pdfStyles.tableRow}>
                <Text style={pdfStyles.colSno}>{sno}</Text>
                <Text style={pdfStyles.colParticulars}>{particulars}</Text>
                <Text style={[
                    pdfStyles.colStatus,
                    isStatusYes && pdfStyles.statusSuccess,
                    isStatusNo && pdfStyles.statusError
                ]}>
                    {displayStatus}
                </Text>
                <Text style={pdfStyles.colRemarks}>{remarks || '—'}</Text>
            </View>
        );
    };

    const renderTableSection = (title, rows) => {
        if (!rows.length) return null;
        return (
            <>
                <Text style={pdfStyles.sectionTitle}>{title}</Text>
                <View style={pdfStyles.table}>
                    <View style={pdfStyles.tableHeader}>
                        <Text style={pdfStyles.colSno}>Sl. No.</Text>
                        <Text style={pdfStyles.colParticulars}>Particulars</Text>
                        <Text style={pdfStyles.colStatus}>Status</Text>
                        <Text style={pdfStyles.colRemarks}>Remarks</Text>
                    </View>
                    {rows.map((row, idx) => (
                        <TableRow key={idx} {...row} />
                    ))}
                </View>
            </>
        );
    };

    // ============ SECTION 1: Society & Registration Details (Rows 1-9) ============
    const socRows = [
        { sno: '1', particulars: 'Transaction Id of Registration and processing fee paid (Rs 30,000)', status: collegeData.transaction_id || formData.transaction_id, remarks: '-', isYesNo: false },
        { sno: '2', particulars: 'Whether a copy of the resolutions of the Society for shifting of the college is uploaded', status: formData.socity_for_shifting, remarks: formData.socity_for_shifting_remarks, isYesNo: true },
        { sno: '3', particulars: 'Whether the Society is registered under societies registration act', status: formData.registration_act, remarks: formData.registration_act_remarks, isYesNo: true },
        { sno: '4', particulars: 'Whether the Bye-laws of the Society is enclosed', status: formData.by_laws, remarks: formData.by_laws_remarks, isYesNo: true },
        { sno: '5', particulars: 'Year of Establishment of the College', status: formData.year_of_establishment || collegeData.year_of_establishment, remarks: '-', isYesNo: false },
        { sno: '6', particulars: 'Whether the permission order for the establishment of the college is enclosed or not', status: formData.enclosed_or_not, remarks: formData.enclosed_or_not_remarks, isYesNo: true },
        { sno: '7', particulars: 'Whether latest University affiliation order (2025-26) is enclosed or not', status: formData.atest_university, remarks: formData.atest_university_remarks, isYesNo: true },
        { sno: '8', particulars: 'The college is located in', status: formData.college_location_type || collegeData.mandal_name, remarks: '-', isYesNo: false },
        { sno: '9', particulars: 'Whether a copy of the last EoP granted is enclosed', status: formData.eop_granted, remarks: formData.eop_granted_remarks, isYesNo: true },
    ];

    // ============ SECTION 2: 10.Land Details (Rows 10a to 10h) ============
    const landRows = [
        { sno: 'a', particulars: 'Whether the society has own land as per norms', status: formData.society_own_land, remarks: formData.society_own_land_remarks, isYesNo: true },
        { sno: 'b', particulars: 'Whether the registered ownership of the land deed is produced', status: formData.ownership_of_the, remarks: formData.ownership_of_the_remarks, isYesNo: true },
        { sno: 'c', particulars: `Details of the land owned: Survey No: ${getSurvey?.survey_no || '-'}, Village: ${getSurvey?.village_name || '-'}, Mandal: ${getSurvey?.mandal_name || '-'}, District: ${getSurvey?.dist_name || '-'}`, status: '-', remarks: '-', isYesNo: false },
        { sno: 'd', particulars: 'Whether the registered document of the land owned in the name of the Society', status: formData.owned_name, remarks: formData.owned_name_remarks, isYesNo: true },
        { sno: 'e', particulars: 'Whether the own land is provided in the same Mandal', status: formData.same_mandal, remarks: formData.same_mandal_remarks, isYesNo: true },
        { sno: 'f', particulars: 'Mention distance of the land from the college building (in km)', status: formData.land_distance_from_cllg || formData.land_distance, remarks: formData.land_distance_remarks, isYesNo: false },
        { sno: 'g', particulars: 'Whether the sketch plan of own land drawn by the licensed surveyor and attested by the MRO is produced', status: formData.licensed_surveyor, remarks: formData.licensed_surveyor_remarks, isYesNo: true },
        { sno: 'h', particulars: 'Whether the schedule of property mentioned in the registered ownership deed is tallying with the sketch plan produced', status: formData.sketch_plan, remarks: formData.sketch_plan_remarks, isYesNo: true },
    ];

    // ============ SECTION 3: Building Accommodation (Rows 11a to 11u) ============
    const buildingRows = [
        { sno: 'a', particulars: 'Nature of Shifting', status: formData.nature_of_shifting, remarks: formData.nature_of_shifting_remarks, isYesNo: false },
        { sno: 'b', particulars: 'Whether the college is proposed to be shifted within the same Mandal', status: formData.proposed_to_be_shifted, remarks: formData.proposed_to_be_shifted_remarks, isYesNo: true },
        { sno: 'c', particulars: 'Whether the new building is permanent and independent building with RCC roof', status: formData.building_is_permanent, remarks: formData.building_is_permanent_remarks, isYesNo: true },
        { sno: 'd', particulars: 'Whether it is stated that the college is located at least 500 mtrs far away from Cinema halls, wine shops, public function halls etc. in the application.', status: formData.located_500_mtrs, remarks: formData.located_500_mtrs_remarks, isYesNo: true },
        { sno: 'e', particulars: 'State whether the proposed building is leased / own', status: formData.building_is_leased, remarks: formData.building_is_leased_remarks, isYesNo: false },
        ...(formData.building_is_leased === 'Leased' ? [
            { sno: 'e1', particulars: 'If leased, Registered lease document of the building is enclosed', status: formData.lease_doc_enclosed, remarks: formData.lease_doc_enclosed_remarks, isYesNo: true },
            { sno: 'e2', particulars: 'Whether the leased document is registered in the name of the society', status: formData.lease_in_society_name, remarks: formData.lease_in_society_name_remarks, isYesNo: true },
            { sno: 'e3', particulars: 'Year of registration of lease document', status: formData.lease_reg_year, remarks: '-', isYesNo: false },
            { sno: 'e4', particulars: 'Validity of the lease period', status: formData.lease_validity, remarks: '-', isYesNo: false },
            { sno: 'e5', particulars: 'Total no. of years completed in leased building from the establishment', status: formData.lease_years_completed, remarks: '-', isYesNo: false },
            { sno: 'e6', particulars: 'Remaining no. of years of the lease period as on date of the application', status: formData.lease_years_remaining, remarks: '-', isYesNo: false },
        ] : []),
        ...(formData.building_is_leased === 'Own' ? [
            { sno: 'e1', particulars: 'If own, Registered sale deed of the building is uploaded', status: formData.sale_deed_uploaded, remarks: formData.sale_deed_uploaded_remarks, isYesNo: true },
            { sno: 'e2', particulars: 'Whether the sale deed is registered in the name of the Society', status: formData.sale_deed_society_name, remarks: formData.sale_deed_society_name_remarks, isYesNo: true },
        ] : []),
        { sno: 'f', particulars: 'Whether the plan of the building accommodation is approved by competent authority for running the institution', status: formData.building_accommodation_approved, remarks: formData.building_accommodation_approved_remarks, isYesNo: true },
        { sno: 'g', particulars: 'Whether approved plan of the building drawn by licensed surveyor and attested by the Principal of a Government College is uploaded', status: formData.drawn_by_licensed, remarks: formData.drawn_by_licensed_remarks, isYesNo: true },
        { sno: 'h', particulars: 'Plinth area of the Building (in Sft)', status: formData.plinth_area, remarks: formData.plinth_area_remarks, isYesNo: false },
        { sno: 'i', particulars: 'Whether a copy of the construction permission order for the leased / own building issued by competent authority is uploaded', status: formData.construction_permission, remarks: formData.construction_permission_remarks, isYesNo: true },
        { sno: 'j', particulars: 'Whether valid Sanitary Certificate is uploaded', status: formData.sanitary_ertificate, remarks: formData.sanitary_ertificate_remarks, isYesNo: true },
        { sno: 'k', particulars: 'Whether latest Geotagged photographs of the building attested by the Principal of a Government College are uploaded', status: formData.geotagged_photographs, remarks: formData.geotagged_photographs_remarks, isYesNo: true },
        { sno: 'l', particulars: 'Whether any other Institution is running in the same building accommodation', status: formData.is_nstitution_running, remarks: formData.is_nstitution_running_remarks, isYesNo: true },
        ...(formData.is_nstitution_running === 'Yes' ? [
            { sno: 'l1', particulars: 'whether NoC from the concerned authority to utilize part of the building, which is not being utilized by the other institution, for running this institution is uploaded', status: formData.authority_to_utilize, remarks: formData.authority_to_utilize_remarks, isYesNo: true },
        ] : []),
        { sno: 'm', particulars: 'Whether the details of existing building accommodation provided in Annexure-I are in accordance with the stipulated norms', status: formData.existing_building, remarks: formData.existing_building_remarks, isYesNo: true },
    ];

    // ============ SECTION 4: PARKING AREA (COMPLETE) ============
    const parkingRows = [
        { sno: 'a', particulars: 'Whether the parking area is available in the same premises', status: formData.parking_available, remarks: formData.parking_available_remarks, isYesNo: true },
        ...(formData.parking_available === 'No' ? [
            { sno: 'a.1', particulars: 'If not in the same premises, whether the parking area provided within a distance of 100 meters thereof', status: formData.parking_within_100meters, remarks: formData.parking_within_100meters_remarks, isYesNo: true },
        ] : []),
        ...(formData.parking_available === 'No' && formData.parking_within_100meters === 'Yes' ? [
            { sno: 'a.2', particulars: 'Whether the document is uploaded for proof of parking area within 100 meters', status: formData.parking_area_doc_uploaded, remarks: formData.final_remarks_remarks, isYesNo: true },
        ] : []),
        ...(formData.parking_available === 'Yes' ? [
            { sno: 'b', particulars: 'Whether 20% of the total built up area is provided for the Parking area', status: formData.parking_area20percent, remarks: formData.parking_area20percent_remarks, isYesNo: true },
        ] : []),
    ];

    // ============ SECTION 5: PLAYGROUND & FIRE SAFETY (COMPLETE) ============
    const playgroundRows = [
        { sno: 'a', particulars: 'Whether the Playground facility is available in the same premises', status: formData.playground_available, remarks: formData.playground_available_remarks, isYesNo: true },
        ...(formData.playground_available === 'Yes' ? [
            { sno: 'a.1', particulars: 'Mention the extent of the playground facility available (sft)', status: formData.playground_extent, remarks: formData.playground_extent_remarks, isYesNo: false },
        ] : []),
        ...(formData.playground_available === 'No' ? [
            { sno: 'b', particulars: 'If not, whether any alternative arrangement for playground is provided within a radius of 5 kms along with bus facility', status: formData.alternative_playground, remarks: formData.alternative_arrangement_remarks, isYesNo: true },
        ] : []),
        ...(formData.alternative_playground === 'Yes' ? [
            { sno: 'c', particulars: 'If yes, whether MoU for utilization of the alternative facility for playground is produced', status: formData.playground_mou, remarks: formData.playground_mou_remarks, isYesNo: true },
        ] : []),
        { sno: '14', particulars: 'Whether valid fire safety certificate', status: formData.parking_exempted_cases, remarks: formData.parking_exempted_cases_remarks, isYesNo: true },
        { sno: '15', particulars: 'Latest affidavit for exempted cases is produced', status: formData.latest_affidavit, remarks: formData.latest_affidavit_remarks, isYesNo: true },
    ];

    // ============ SECTION 6: CORPUS FUND ============
    const corpusRows = [
        { sno: 'a', particulars: 'Whether the college was exempted from paying the Corpus Fund', status: formData.corpus_fund, remarks: formData.corpus_fund_remarks, isYesNo: true },
        ...(formData.corpus_fund === 'Yes' ? [
            { sno: 'b', particulars: 'Whether the rural/tribal area certificate issued by MRO is produced', status: formData.certificate_issued, remarks: formData.certificate_issued_remarks, isYesNo: true },
        ] : []),
    ];

    // ============ SECTION 7: INFRASTRUCTURE & ACADEMIC FACILITIES (COMPLETE) ============
    const infraRows = [
        { sno: '17.a', particulars: 'Whether lab equipment provided for all the subjects', status: formData.lab_equipment_provided, remarks: formData.lab_equipment_provided_remarks, isYesNo: true },
        ...(formData.lab_equipment_provided === 'No' ? [
            { sno: '17.b', particulars: 'Mention the subjects that lack equipment', status: formData.lab_lack_equipments, remarks: formData.lab_lack_equipments_remarks, isYesNo: false },
        ] : []),
        { sno: '18.a', particulars: 'Whether library books are provided for all the subjects', status: formData.library_books_provided, remarks: formData.library_books_provided_remarks, isYesNo: true },
        ...(formData.library_books_provided === 'No' ? [
            { sno: '18.b', particulars: 'Mention the subjects that lack books', status: formData.library_lack_equipments, remarks: formData.library_lack_equipments_remarks, isYesNo: false },
        ] : []),
        { sno: '19.a', particulars: 'Whether adequate teaching and supporting staff are provided to run the colleges as per the University / UGC norms', status: formData.staff_ugc_norms, remarks: formData.staff_ugc_norms_remarks, isYesNo: true },
        ...(formData.staff_ugc_norms === 'No' ? [
            { sno: '19.b', particulars: 'Mention the deficiencies', status: formData.staff_teaching_deficiencies, remarks: formData.staff_teaching_deficiencies_remarks, isYesNo: false },
        ] : []),
        { sno: '20', particulars: 'Whether scholarship details are provided as per Annexure -III of the application', status: formData.staff_as_per_annexure, remarks: formData.staff_as_per_annexure_remarks, isYesNo: true },
        { sno: '21', particulars: 'Whether the college secured any University ranks in the last academic year', status: formData.staff_university_rank, remarks: formData.staff_university_rank_remarks, isYesNo: true },
        { sno: '22', particulars: 'Whether the college conducted extension activities during the last academic year', status: formData.staff_extension_activities, remarks: formData.staff_extension_activities_remarks, isYesNo: true },
        { sno: '23', particulars: 'Whether the college has functional website', status: formData.staff_functional_website, remarks: formData.staff_functional_website_remarks, isYesNo: true },
    ];

    // ============ SECTION 8: FINAL REMARKS & STATUS ============
    const finalRows = [
        { sno: '', particulars: 'Final Remarks', status: formData.final_remarks, remarks: '', isYesNo: false },
        { sno: '', particulars: 'Final Status', status: formData.final_status, remarks: '', isYesNo: true },
    ];

    // ============ Accommodation Table ============
    const renderBuildingTable = () => {
        if (!accomidation.length) return null;
        return (
            <>
                <Text style={pdfStyles.sectionTitle}>Accommodation required & provided for existing PG programmes</Text>
                <View style={pdfStyles.table}>
                    <View style={pdfStyles.tableHeader}>
                        <Text style={{ width: '8%', paddingLeft: 4 }}>Sl.No</Text>
                        <Text style={{ width: '27%', paddingLeft: 4 }}>Type of the room</Text>
                        <Text style={{ width: '15%', paddingLeft: 4 }}>Rooms Required</Text>
                        <Text style={{ width: '15%', paddingLeft: 4 }}>Area Required (sft)</Text>
                        <Text style={{ width: '15%', paddingLeft: 4 }}>Rooms Provided</Text>
                        <Text style={{ width: '20%', paddingLeft: 4 }}>Sufficient as per norms</Text>
                        <Text style={{ width: '20%', paddingLeft: 4 }}>Remarks</Text>
                    </View>
                    {accomidation.map((item, idx) => (
                        <View key={idx} style={pdfStyles.tableRow}>
                            <Text style={{ width: '8%', paddingLeft: 4 }}>{idx + 1}</Text>
                            <Text style={{ width: '27%', paddingLeft: 4 }}>{item.particular_name || '-'}</Text>
                            <Text style={{ width: '15%', paddingLeft: 4 }}>{item.no_of_rooms || '-'}</Text>
                            <Text style={{ width: '15%', paddingLeft: 4 }}>{item.total_area || '-'}</Text>
                            <Text style={{ width: '15%', paddingLeft: 4 }}>{item.no_of_rooms || '-'}</Text>
                            <Text style={{ width: '20%', paddingLeft: 4 }}>{item.sufficient_as_per_norms || '-'}</Text>
                            <Text style={{ width: '20%', paddingLeft: 4 }}>{item.remarks || '-'}</Text>
                        </View>
                    ))}
                </View>
            </>
        );
    };

    // ============ UG Programmes Table ============
    const renderUGProgrammes = () => {
        if (!ugCourses.length) return null;
        return (
            <>
                <Text style={pdfStyles.sectionTitle}>Details of the UG Programmes</Text>
                <View style={pdfStyles.table}>
                    <View style={pdfStyles.tableHeader}>
                        <Text style={{ width: '10%', paddingLeft: 4 }}>Sl.No</Text>
                        <Text style={{ width: '50%', paddingLeft: 4 }}>Type of the UG Programme</Text>
                        <Text style={{ width: '20%', paddingLeft: 4 }}>Total No. of programmes</Text>
                        <Text style={{ width: '20%', paddingLeft: 4 }}>Sanctioned Intake</Text>
                    </View>
                    {ugCourses.map((c, i) => (
                        <View key={i} style={pdfStyles.tableRow}>
                            <Text style={{ width: '10%', paddingLeft: 4 }}>{i + 1}</Text>
                            <Text style={{ width: '50%', paddingLeft: 4 }}>{c.course_cat_name}</Text>
                            <Text style={{ width: '20%', paddingLeft: 4 }}>{c.count}</Text>
                            <Text style={{ width: '20%', paddingLeft: 4 }}>{c.sum}</Text>
                        </View>
                    ))}
                </View>
            </>
        );
    };

    // ============ PG Programmes Table ============
    const renderPGProgrammes = () => {
        if (!pgCourses.length) return null;
        return (
            <>
                <Text style={pdfStyles.sectionTitle}>Details of the PG Programmes</Text>
                <View style={pdfStyles.table}>
                    <View style={pdfStyles.tableHeader}>
                        <Text style={{ width: '10%', paddingLeft: 4 }}>Sl.No</Text>
                        <Text style={{ width: '50%', paddingLeft: 4 }}>Type of the PG Programme</Text>
                        <Text style={{ width: '20%', paddingLeft: 4 }}>Total No. of programmes</Text>
                        <Text style={{ width: '20%', paddingLeft: 4 }}>Sanctioned Intake</Text>
                    </View>
                    {pgCourses.map((c, i) => (
                        <View key={i} style={pdfStyles.tableRow}>
                            <Text style={{ width: '10%', paddingLeft: 4 }}>{i + 1}</Text>
                            <Text style={{ width: '50%', paddingLeft: 4 }}>{c.course_cat_name}</Text>
                            <Text style={{ width: '20%', paddingLeft: 4 }}>{c.count}</Text>
                            <Text style={{ width: '20%', paddingLeft: 4 }}>{c.sum}</Text>
                        </View>
                    ))}
                </View>
            </>
        );
    };

    // ============ Corpus Fund Installments Table ============
    const renderCorpusInstallments = () => {
        if (!corpusInstall.length) return null;
        const inst1 = corpusInstall.find(i => i.installment_no === 1) || {};
        const inst2 = corpusInstall.find(i => i.installment_no === 2) || {};
        const inst3 = corpusInstall.find(i => i.installment_no === 3) || {};
        return (
            <>
                <Text style={pdfStyles.sectionTitle}>For Degree Colleges established before 2021-22 - Corpus Fund Installments</Text>
                <View style={pdfStyles.table}>
                    <View style={pdfStyles.tableHeader}>
                        <Text style={{ width: '30%', paddingLeft: 4 }}>Required Information</Text>
                        <Text style={{ width: '23%', paddingLeft: 4 }}>First Installment</Text>
                        <Text style={{ width: '23%', paddingLeft: 4 }}>Second Installment</Text>
                        <Text style={{ width: '24%', paddingLeft: 4 }}>Third Installment</Text>
                    </View>
                    <View style={pdfStyles.tableRow}><Text style={{ width: '30%', paddingLeft: 4 }}>Amount</Text><Text style={{ width: '23%', paddingLeft: 4 }}>{inst1.amount || '-'}</Text><Text style={{ width: '23%', paddingLeft: 4 }}>{inst2.amount || '-'}</Text><Text style={{ width: '24%', paddingLeft: 4 }}>{inst3.amount || '-'}</Text></View>
                    <View style={pdfStyles.tableRow}><Text style={{ width: '30%', paddingLeft: 4 }}>Period (from - to)</Text><Text style={{ width: '23%', paddingLeft: 4 }}>{inst1.from_date ? `${inst1.from_date} - ${inst1.to_date}` : '-'}</Text><Text style={{ width: '23%', paddingLeft: 4 }}>{inst2.from_date ? `${inst2.from_date} - ${inst2.to_date}` : '-'}</Text><Text style={{ width: '24%', paddingLeft: 4 }}>{inst3.from_date ? `${inst3.from_date} - ${inst3.to_date}` : '-'}</Text></View>
                    <View style={pdfStyles.tableRow}><Text style={{ width: '30%', paddingLeft: 4 }}>Date of FDR</Text><Text style={{ width: '23%', paddingLeft: 4 }}>{inst1.date_of_fdr || '-'}</Text><Text style={{ width: '23%', paddingLeft: 4 }}>{inst2.date_of_fdr || '-'}</Text><Text style={{ width: '24%', paddingLeft: 4 }}>{inst3.date_of_fdr || '-'}</Text></View>
                    <View style={pdfStyles.tableRow}><Text style={{ width: '30%', paddingLeft: 4 }}>Name & address of the Issuing Bank</Text><Text style={{ width: '23%', paddingLeft: 4 }}>{inst1.bank_address || '-'}</Text><Text style={{ width: '23%', paddingLeft: 4 }}>{inst2.bank_address || '-'}</Text><Text style={{ width: '24%', paddingLeft: 4 }}>{inst3.bank_address || '-'}</Text></View>
                    <View style={pdfStyles.tableRow}><Text style={{ width: '30%', paddingLeft: 4 }}>IFSC CODE</Text><Text style={{ width: '23%', paddingLeft: 4 }}>{inst1.ifsc || '-'}</Text><Text style={{ width: '23%', paddingLeft: 4 }}>{inst2.ifsc || '-'}</Text><Text style={{ width: '24%', paddingLeft: 4 }}>{inst3.ifsc || '-'}</Text></View>
                    <View style={pdfStyles.tableRow}><Text style={{ width: '30%', paddingLeft: 4 }}>Date of maturity</Text><Text style={{ width: '23%', paddingLeft: 4 }}>{inst1.date_of_maturity || '-'}</Text><Text style={{ width: '23%', paddingLeft: 4 }}>{inst2.date_of_maturity || '-'}</Text><Text style={{ width: '24%', paddingLeft: 4 }}>{inst3.date_of_maturity || '-'}</Text></View>
                    <View style={pdfStyles.tableRow}><Text style={{ width: '30%', paddingLeft: 4 }}>Date of renewal</Text><Text style={{ width: '23%', paddingLeft: 4 }}>{inst1.date_of_renewal || '-'}</Text><Text style={{ width: '23%', paddingLeft: 4 }}>{inst2.date_of_renewal || '-'}</Text><Text style={{ width: '24%', paddingLeft: 4 }}>{inst3.date_of_renewal || '-'}</Text></View>
                </View>
            </>
        );
    };

    // ============ UG/PG Corpus Fund Tables ============
    const renderCorpusUGPGTables = () => {
        return (
            <>
                {ugCorpusFunds.length > 0 && (
                    <>
                        <Text style={pdfStyles.sectionTitle}>For Degree Colleges / programmes sanctioned after 2021-22 - UG Programmes Corpus Fund</Text>
                        <View style={pdfStyles.table}>
                            <View style={pdfStyles.tableHeader}>
                                <Text style={{ width: '8%', paddingLeft: 4 }}>Sl.No</Text>
                                <Text style={{ width: '22%', paddingLeft: 4 }}>Programme</Text>
                                <Text style={{ width: '15%', paddingLeft: 4 }}>Amount</Text>
                                <Text style={{ width: '20%', paddingLeft: 4 }}>Period</Text>
                                <Text style={{ width: '20%', paddingLeft: 4 }}>FDR No.</Text>
                                <Text style={{ width: '15%', paddingLeft: 4 }}>Date</Text>
                            </View>
                            {ugCorpusFunds.map((c, i) => (
                                <View key={i} style={pdfStyles.tableRow}>
                                    <Text style={{ width: '8%', paddingLeft: 4 }}>{i + 1}</Text>
                                    <Text style={{ width: '22%', paddingLeft: 4 }}>{c.course_name}</Text>
                                    <Text style={{ width: '15%', paddingLeft: 4 }}>₹{c.amount}</Text>
                                    <Text style={{ width: '20%', paddingLeft: 4 }}>{c.period_from} to {c.period_to}</Text>
                                    <Text style={{ width: '20%', paddingLeft: 4 }}>{c.fdr_bank_number}</Text>
                                    <Text style={{ width: '15%', paddingLeft: 4 }}>{c.corpus_date}</Text>
                                </View>
                            ))}
                        </View>
                    </>
                )}
                {pgBeforeCorpus.length > 0 && (
                    <>
                        <Text style={pdfStyles.sectionTitle}>For existing PG Programmes sanctioned before 2021-22 - Corpus Fund</Text>
                        <View style={pdfStyles.table}>
                            <View style={pdfStyles.tableHeader}>
                                <Text style={{ width: '8%', paddingLeft: 4 }}>Sl.No</Text>
                                <Text style={{ width: '22%', paddingLeft: 4 }}>Programme</Text>
                                <Text style={{ width: '15%', paddingLeft: 4 }}>Amount</Text>
                                <Text style={{ width: '20%', paddingLeft: 4 }}>Period</Text>
                                <Text style={{ width: '20%', paddingLeft: 4 }}>FDR No.</Text>
                                <Text style={{ width: '15%', paddingLeft: 4 }}>Date</Text>
                            </View>
                            {pgBeforeCorpus.map((c, i) => (
                                <View key={i} style={pdfStyles.tableRow}>
                                    <Text style={{ width: '8%', paddingLeft: 4 }}>{i + 1}</Text>
                                    <Text style={{ width: '22%', paddingLeft: 4 }}>{c.course_name}</Text>
                                    <Text style={{ width: '15%', paddingLeft: 4 }}>₹{c.amount}</Text>
                                    <Text style={{ width: '20%', paddingLeft: 4 }}>{c.period_from} to {c.period_to}</Text>
                                    <Text style={{ width: '20%', paddingLeft: 4 }}>{c.fdr_bank_number}</Text>
                                    <Text style={{ width: '15%', paddingLeft: 4 }}>{c.corpus_date}</Text>
                                </View>
                            ))}
                        </View>
                    </>
                )}
                {pgAfterCorpus.length > 0 && (
                    <>
                        <Text style={pdfStyles.sectionTitle}>For existing PG Programmes sanctioned after 2021-22 - Corpus Fund</Text>
                        <View style={pdfStyles.table}>
                            <View style={pdfStyles.tableHeader}>
                                <Text style={{ width: '8%', paddingLeft: 4 }}>Sl.No</Text>
                                <Text style={{ width: '22%', paddingLeft: 4 }}>Programme</Text>
                                <Text style={{ width: '15%', paddingLeft: 4 }}>Amount</Text>
                                <Text style={{ width: '20%', paddingLeft: 4 }}>Period</Text>
                                <Text style={{ width: '20%', paddingLeft: 4 }}>FDR No.</Text>
                                <Text style={{ width: '15%', paddingLeft: 4 }}>Date</Text>
                            </View>
                            {pgAfterCorpus.map((c, i) => (
                                <View key={i} style={pdfStyles.tableRow}>
                                    <Text style={{ width: '8%', paddingLeft: 4 }}>{i + 1}</Text>
                                    <Text style={{ width: '22%', paddingLeft: 4 }}>{c.course_name}</Text>
                                    <Text style={{ width: '15%', paddingLeft: 4 }}>₹{c.amount}</Text>
                                    <Text style={{ width: '20%', paddingLeft: 4 }}>{c.period_from} to {c.period_to}</Text>
                                    <Text style={{ width: '20%', paddingLeft: 4 }}>{c.fdr_bank_number}</Text>
                                    <Text style={{ width: '15%', paddingLeft: 4 }}>{c.corpus_date}</Text>
                                </View>
                            ))}
                        </View>
                    </>
                )}
            </>
        );
    };

    // ============ Deficiencies Table ============
    const renderDeficiencies = () => {
        if (formData.final_status !== 'DeficiencyIdentified' || deficiencies.length === 0) return null;
        return (
            <>
                <Text style={[pdfStyles.sectionTitle, { backgroundColor: '#ffebee', color: '#c62828' }]}>Deficiencies Identified</Text>
                <View style={pdfStyles.table}>
                    <View style={pdfStyles.tableHeader}>
                        <Text style={{ width: '15%', paddingLeft: 4 }}>S.No</Text>
                        <Text style={{ width: '85%', paddingLeft: 4 }}>Deficiency Remarks</Text>
                    </View>
                    {deficiencies.map((def, idx) => (
                        <View key={idx} style={pdfStyles.tableRow}>
                            <Text style={{ width: '15%', paddingLeft: 4 }}>{idx + 1}</Text>
                            <Text style={{ width: '85%', paddingLeft: 4 }}>{def.remarks}</Text>
                        </View>
                    ))}
                </View>
            </>
        );
    };

    return (
        <Document>
            <Page size="A4" style={pdfStyles.page}>
                <View style={pdfStyles.header}>
                    <Text style={pdfStyles.headerTitle}>{collegeData.inst_name || 'College Name'}</Text>
                    <Text style={pdfStyles.headerSubtitle}>Shifting Scrutiny Report for the Academic Year 2026-27</Text>
                    <small>Generated on {new Date().toLocaleDateString('en-GB')}</small>

                   
                  
                </View>

                {/* Basic Info */}
                <Text style={pdfStyles.sectionTitle}>College Basic Information</Text>
                <View style={pdfStyles.table}>
                    <View style={pdfStyles.tableRow}>
                        <Text style={{ width: '50%', paddingLeft: 4 }}><Text style={{ fontWeight: 'bold' }}>Name of the Society & Address:</Text> {collegeData.society_name || '-'} - {collegeData.mandal_name || '-'}</Text>
                        <Text style={{ width: '50%', paddingLeft: 4 }}><Text style={{ fontWeight: 'bold' }}>Name and address of the Existing Degree College:</Text> {collegeData.inst_address || '-'}</Text>
                    </View>
                    <View style={pdfStyles.tableRow}>
                        <Text style={{ width: '100%', paddingLeft: 4 }}><Text style={{ fontWeight: 'bold' }}>Name & New Address of the Degree College Proposed to Shift:</Text> Mandal: {shiftAddr.mandal_name || '-'}, District: {shiftAddr.district_name || '-'}, Pincode: {shiftAddr.new_pincode || '-'}, Address: {shiftAddr.address_ofnew_location || '-'}</Text>
                    </View>
                </View>

                {/* ALL SECTIONS */}
                {renderTableSection('Society & Registration Details', socRows)}
                {renderTableSection(' 10.Land', landRows)}
                {renderTableSection(' 11.New Building Accommodation', buildingRows)}
                {renderBuildingTable()}
                {renderUGProgrammes()}
                {renderPGProgrammes()}
                {renderTableSection(' 12.Parking Area', parkingRows)}
                {renderTableSection(' 13.Playground & Fire Safety', playgroundRows)}
                {renderTableSection('16. Corpus Fund', corpusRows)}
                {renderCorpusInstallments()}
                {renderCorpusUGPGTables()}
                {renderTableSection('17. Infrastructure & Academic Facilities Survey', infraRows)}
                {renderTableSection('Final Remarks & Status', finalRows)}
                {renderDeficiencies()}

                <View style={pdfStyles.footer}>
                    <Text>College Code: {formData.coll_code || collegeData.coll_code || ''} | This is a system-generated shifting scrutiny report.</Text>
                </View>
            </Page>
        </Document>
    );
};

// =============== WEB VIEW (COMPLETE with ALL Sections) ===============
export const WebViewReport = ({ data, data2, rowData, defData, acc, mainData, getSurvey, shiftAddr }) => {
    const formData = data2?.[0] || {};
    const apiExtra = data || {};
    const collegeData = apiExtra.data?.[0] || rowData || {};
    const def = Array.isArray(defData) ? defData : (apiExtra.def || []);

    // SAFE extraction with null checks for insert_by and insert_time
    const firstDeficiency = def[0] || {};
    const insertBy = firstDeficiency.insert_by ||
        firstDeficiency.inserted_by ||
        formData.insert_by ||
        formData.inserted_by ||
        collegeData.insert_by ||
        'N/A';
    const formatDateToDMY = (dateValue) => {
        if (!dateValue) return 'N/A';
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return 'N/A';
        return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
    };
    const insertTime = formatDateToDMY(firstDeficiency.insert_time) ||
        firstDeficiency.inserted_time ||
        firstDeficiency.inserted_date ||
        firstDeficiency.created_at ||
        formData.insert_time ||
        formData.inserted_time ||
        formData.created_at ||
        new Date().toLocaleString();

    const accomidation = Array.isArray(acc) ? acc : (apiExtra.accom || []);

    const ugCourses = Array.isArray(apiExtra.ugCourses) ? apiExtra.ugCourses : [];
    const pgCourses = Array.isArray(apiExtra.pgCourses) ? apiExtra.pgCourses : [];
    const corpusInstall = Array.isArray(apiExtra.corpusFundInstall) ? apiExtra.corpusFundInstall : [];
    const allCorpus = Array.isArray(apiExtra.corpusfundUGPG) ? apiExtra.corpusfundUGPG : [];

    const ugCorpusFunds = Array.isArray(allCorpus) ? allCorpus.filter(i => i.coll_type?.replace(/"/g, '').trim() === 'UG') : [];
    const pgBeforeCorpus = Array.isArray(allCorpus) ? allCorpus.filter(i => i.coll_type?.replace(/"/g, '').trim() === 'PG before') : [];
    const pgAfterCorpus = Array.isArray(allCorpus) ? allCorpus.filter(i => i.coll_type?.replace(/"/g, '').trim() === 'PG after') : [];

    const formatValue = (val) => {
        if (val === undefined || val === null || val === '') return '—';
        if (val === 'Yes' || val === 'YES') return 'Yes';
        if (val === 'No' || val === 'NO') return 'No';
        return val;
    };

    const TableRow = ({ sno, particulars, status, remarks, isYesNo }) => (
        <tr>
            <td className="text-center">{sno}</td>
            <td>{particulars}</td>
            <td className={isYesNo ? (status === 'Yes' ? 'text-success fw-bold' : (status === 'No' ? 'text-danger fw-bold' : '')) : ''}>
                {isYesNo ? formatValue(status) : (status || '—')}
            </td>
            <td>{remarks || '—'}</td>
        </tr>
    );

    const socRows = [
        { sno: '1', particulars: 'Transaction Id of Registration and processing fee paid (Rs 30,000)', status: collegeData.transaction_id || formData.transaction_id, remarks: '-', isYesNo: false },
        { sno: '2', particulars: 'Whether a copy of the resolutions of the Society for shifting of the college is uploaded', status: formData.socity_for_shifting, remarks: formData.socity_for_shifting_remarks, isYesNo: true },
        { sno: '3', particulars: 'Whether the Society is registered under societies registration act', status: formData.registration_act, remarks: formData.registration_act_remarks, isYesNo: true },
        { sno: '4', particulars: 'Whether the Bye-laws of the Society is enclosed', status: formData.by_laws, remarks: formData.by_laws_remarks, isYesNo: true },
        { sno: '5', particulars: 'Year of Establishment of the College', status: formData.year_of_establishment || collegeData.year_of_establishment, remarks: '-', isYesNo: false },
        { sno: '6', particulars: 'Whether the permission order for the establishment of the college is enclosed or not', status: formData.enclosed_or_not, remarks: formData.enclosed_or_not_remarks, isYesNo: true },
        { sno: '7', particulars: 'Whether latest University affiliation order (2025-26) is enclosed or not', status: formData.atest_university, remarks: formData.atest_university_remarks, isYesNo: true },
        { sno: '8', particulars: 'The college is located in', status: formData.college_location_type || collegeData.mandal_name, remarks: '-', isYesNo: false },
        { sno: '9', particulars: 'Whether a copy of the last EoP granted is enclosed', status: formData.eop_granted, remarks: formData.eop_granted_remarks, isYesNo: true },
    ];

    const landRows = [
        { sno: 'a', particulars: 'Whether the society has own land as per norms', status: formData.society_own_land, remarks: formData.society_own_land_remarks, isYesNo: true },
        { sno: 'b', particulars: 'Whether the registered ownership of the land deed is produced', status: formData.ownership_of_the, remarks: formData.ownership_of_the_remarks, isYesNo: true },
        { sno: 'c', particulars: `Details of the land owned: Survey No: ${getSurvey?.survey_no || '-'}, Village: ${getSurvey?.village_name || '-'}, Mandal: ${getSurvey?.mandal_name || '-'}, District: ${getSurvey?.dist_name || '-'}`, status: '-', remarks: '-', isYesNo: false },
        { sno: 'd', particulars: 'Whether the registered document of the land owned in the name of the Society', status: formData.owned_name, remarks: formData.owned_name_remarks, isYesNo: true },
        { sno: 'e', particulars: 'Whether the own land is provided in the same Mandal', status: formData.same_mandal, remarks: formData.same_mandal_remarks, isYesNo: true },
        { sno: 'f', particulars: 'Mention distance of the land from the college building (in km)', status: formData.land_distance_from_cllg || formData.land_distance, remarks: formData.land_distance_remarks, isYesNo: false },
        { sno: 'g', particulars: 'Whether the sketch plan of own land drawn by the licensed surveyor and attested by the MRO is produced', status: formData.licensed_surveyor, remarks: formData.licensed_surveyor_remarks, isYesNo: true },
        { sno: 'h', particulars: 'Whether the schedule of property mentioned in the registered ownership deed is tallying with the sketch plan produced', status: formData.sketch_plan, remarks: formData.sketch_plan_remarks, isYesNo: true },
    ];

    const buildingRows = [
        { sno: 'a', particulars: 'Nature of Shifting', status: formData.nature_of_shifting, remarks: formData.nature_of_shifting_remarks, isYesNo: false },
        { sno: 'b', particulars: 'Whether the college is proposed to be shifted within the same Mandal', status: formData.proposed_to_be_shifted, remarks: formData.proposed_to_be_shifted_remarks, isYesNo: true },
        { sno: 'c', particulars: 'Whether the new building is permanent and independent building with RCC roof', status: formData.building_is_permanent, remarks: formData.building_is_permanent_remarks, isYesNo: true },
        { sno: 'd', particulars: 'Whether it is stated that the college is located at least 500 mtrs far away from Cinema halls, wine shops, public function halls etc. in the application.', status: formData.located_500_mtrs, remarks: formData.located_500_mtrs_remarks, isYesNo: true },
        { sno: 'e', particulars: 'State whether the proposed building is leased / own', status: formData.building_is_leased, remarks: formData.building_is_leased_remarks, isYesNo: false },
        ...(formData.building_is_leased === 'Leased' ? [
            { sno: 'e1', particulars: 'If leased, Registered lease document of the building is enclosed', status: formData.lease_doc_enclosed, remarks: formData.lease_doc_enclosed_remarks, isYesNo: true },
            { sno: 'e2', particulars: 'Whether the leased document is registered in the name of the society', status: formData.lease_in_society_name, remarks: formData.lease_in_society_name_remarks, isYesNo: true },
        ] : []),
        ...(formData.building_is_leased === 'Own' ? [
            { sno: 'e1', particulars: 'If own, Registered sale deed of the building is uploaded', status: formData.sale_deed_uploaded, remarks: formData.sale_deed_uploaded_remarks, isYesNo: true },
            { sno: 'e2', particulars: 'Whether the sale deed is registered in the name of the Society', status: formData.sale_deed_society_name, remarks: formData.sale_deed_society_name_remarks, isYesNo: true },
        ] : []),
        { sno: 'f', particulars: 'Whether the plan of the building accommodation is approved by competent authority for running the institution', status: formData.building_accommodation_approved, remarks: formData.building_accommodation_approved_remarks, isYesNo: true },
        { sno: 'g', particulars: 'Whether approved plan of the building drawn by licensed surveyor and attested by the Principal of a Government College is uploaded', status: formData.drawn_by_licensed, remarks: formData.drawn_by_licensed_remarks, isYesNo: true },
        { sno: 'h', particulars: 'Plinth area of the Building (in Sft)', status: formData.plinth_area, remarks: formData.plinth_area_remarks, isYesNo: false },
        { sno: 'i', particulars: 'Whether a copy of the construction permission order for the leased / own building issued by competent authority is uploaded', status: formData.construction_permission, remarks: formData.construction_permission_remarks, isYesNo: true },
        { sno: 'j', particulars: 'Whether valid Sanitary Certificate is uploaded', status: formData.sanitary_ertificate, remarks: formData.sanitary_ertificate_remarks, isYesNo: true },
        { sno: 'k', particulars: 'Whether latest Geotagged photographs of the building attested by the Principal of a Government College are uploaded', status: formData.geotagged_photographs, remarks: formData.geotagged_photographs_remarks, isYesNo: true },
        { sno: 'l', particulars: 'Whether any other Institution is running in the same building accommodation', status: formData.is_nstitution_running, remarks: formData.is_nstitution_running_remarks, isYesNo: true },
        ...(formData.is_nstitution_running === 'Yes' ? [
            { sno: 'l1', particulars: 'whether NoC from the concerned authority to utilize part of the building, which is not being utilized by the other institution, for running this institution is uploaded', status: formData.authority_to_utilize, remarks: formData.authority_to_utilize_remarks, isYesNo: true },
        ] : []),
        { sno: 'm', particulars: 'Whether the details of existing building accommodation provided in Annexure-I are in accordance with the stipulated norms', status: formData.existing_building, remarks: formData.existing_building_remarks, isYesNo: true },
    ];

    const parkingRows = [
        { sno: 'a', particulars: 'Whether the parking area is available in the same premises', status: formData.parking_available, remarks: formData.parking_available_remarks, isYesNo: true },
        ...(formData.parking_available === 'No' ? [
            { sno: 'a.1', particulars: 'If not in the same premises, whether the parking area provided within a distance of 100 meters thereof', status: formData.parking_within_100meters, remarks: formData.parking_within_100meters_remarks, isYesNo: true },
        ] : []),
        ...(formData.parking_available === 'Yes' ? [
            { sno: 'b', particulars: 'Whether 20% of the total built up area is provided for the Parking area', status: formData.parking_area20percent, remarks: formData.parking_area20percent_remarks, isYesNo: true },
        ] : []),
    ];

    const playgroundRows = [
        { sno: 'a', particulars: 'Whether the Playground facility is available in the same premises', status: formData.playground_available, remarks: formData.playground_available_remarks, isYesNo: true },
        ...(formData.playground_available === 'Yes' ? [
            { sno: 'a.1', particulars: 'Mention the extent of the playground facility available (sft)', status: formData.playground_extent, remarks: formData.playground_extent_remarks, isYesNo: false },
        ] : []),
        ...(formData.playground_available === 'No' ? [
            { sno: 'b', particulars: 'If not, whether any alternative arrangement for playground is provided within a radius of 5 kms along with bus facility', status: formData.alternative_playground, remarks: formData.alternative_arrangement_remarks, isYesNo: true },
        ] : []),
        ...(formData.alternative_playground === 'Yes' ? [
            { sno: 'c', particulars: 'If yes, whether MoU for utilization of the alternative facility for playground is produced', status: formData.playground_mou, remarks: formData.playground_mou_remarks, isYesNo: true },
        ] : []),
        { sno: '14', particulars: 'Whether valid fire safety certificate', status: formData.parking_exempted_cases, remarks: formData.parking_exempted_cases_remarks, isYesNo: true },
        { sno: '15', particulars: 'Latest affidavit for exempted cases is produced', status: formData.latest_affidavit, remarks: formData.latest_affidavit_remarks, isYesNo: true },
    ];

    const corpusRows = [
        { sno: 'a', particulars: 'Whether the college was exempted from paying the Corpus Fund', status: formData.corpus_fund, remarks: formData.corpus_fund_remarks, isYesNo: true },
        ...(formData.corpus_fund === 'Yes' ? [
            { sno: 'b', particulars: 'Whether the rural/tribal area certificate issued by MRO is produced', status: formData.certificate_issued, remarks: formData.certificate_issued_remarks, isYesNo: true },
        ] : []),
    ];

    const infraRows = [
        { sno: '17.a', particulars: 'Whether lab equipment provided for all the subjects', status: formData.lab_equipment_provided, remarks: formData.lab_equipment_provided_remarks, isYesNo: true },
        ...(formData.lab_equipment_provided === 'No' ? [
            { sno: '17.b', particulars: 'Mention the subjects that lack equipment', status: formData.lab_lack_equipments, remarks: formData.lab_lack_equipments_remarks, isYesNo: false },
        ] : []),
        { sno: '18.a', particulars: 'Whether library books are provided for all the subjects', status: formData.library_books_provided, remarks: formData.library_books_provided_remarks, isYesNo: true },
        ...(formData.library_books_provided === 'No' ? [
            { sno: '18.b', particulars: 'Mention the subjects that lack books', status: formData.library_lack_equipments, remarks: formData.library_lack_equipments_remarks, isYesNo: false },
        ] : []),
        { sno: '19.a', particulars: 'Whether adequate teaching and supporting staff are provided to run the colleges as per the University / UGC norms', status: formData.staff_ugc_norms, remarks: formData.staff_ugc_norms_remarks, isYesNo: true },
        ...(formData.staff_ugc_norms === 'No' ? [
            { sno: '19.b', particulars: 'Mention the deficiencies', status: formData.staff_teaching_deficiencies, remarks: formData.staff_teaching_deficiencies_remarks, isYesNo: false },
        ] : []),
        { sno: '20', particulars: 'Whether scholarship details are provided as per Annexure -III of the application', status: formData.staff_as_per_annexure, remarks: formData.staff_as_per_annexure_remarks, isYesNo: true },
        { sno: '21', particulars: 'Whether the college secured any University ranks in the last academic year', status: formData.staff_university_rank, remarks: formData.staff_university_rank_remarks, isYesNo: true },
        { sno: '22', particulars: 'Whether the college conducted extension activities during the last academic year', status: formData.staff_extension_activities, remarks: formData.staff_extension_activities_remarks, isYesNo: true },
        { sno: '23', particulars: 'Whether the college has functional website', status: formData.staff_functional_website, remarks: formData.staff_functional_website_remarks, isYesNo: true },
    ];

    const renderTableSectionWeb = (title, rows) => {
        if (!rows.length) return null;
        return (
            <>
                <h5 className="mt-4">{title}</h5>
                <table className="table table-bordered">
                    <thead className="table-primary">
                        <tr>
                            <th style={{ width: '8%' }}>S.No</th>
                            <th>Particulars</th>
                            <th style={{ width: '25%' }}>Status</th>
                            <th style={{ width: '30%' }}>Remarks</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, idx) => <TableRow key={idx} {...row} />)}
                    </tbody>
                </table>
            </>
        );
    };

    return (
        <div className="container my-4">
            <div className="card shadow-sm">
                {/* <div className="card-header bg-success text-white">
                    <h3 className="mb-0">{rowData?.inst_name || collegeData.inst_name || 'College Name'} – Shifting Scrutiny Report</h3>
                    <small>Generated on {new Date().toLocaleDateString('en-GB')}</small>

                    <div style={{ marginTop: 5 }}>
                        <small><strong>Inspected By:</strong> {formData.user_name} -Cfms Id:{insertBy} </small><br />
                        <small><strong>Date Of Inspection :</strong> {insertTime}</small>
                    </div>
                </div> */}
                <div className="card-header bg-success text-white">
                    <h4 className="mb-0">{rowData?.inst_name || collegeData.inst_name || 'College Name'} –{formData.coll_code || collegeData.coll_code || ''} Shifting Scrutiny Report</h4>
                    <small>
                        Generated on {new Date().toLocaleDateString('en-GB')} |
                       
                    </small>
                </div>
                <div className="card-body">
                    <h5>College Basic Information</h5>
                    <table className="table table-bordered">
                        <tbody>
                            <tr><th>Name of the Society & Address</th><td colSpan="3">{collegeData.society_name || '-'} - {collegeData.mandal_name || '-'}</td></tr>
                            <tr><th>Name and address of the Existing Degree College</th><td colSpan="3">{collegeData.inst_address || '-'}</td></tr>
                            <tr><th>Name & New Address of the Degree College Proposed to Shift</th><td colSpan="3">Mandal: {shiftAddr.new_mandal || '-'}, District: {shiftAddr.new_district || '-'}, Pincode: {shiftAddr.new_pincode || '-'}, Address: {shiftAddr.address_ofnew_location || '-'}</td></tr>
                        </tbody>
                    </table>

                    {renderTableSectionWeb('Society & Registration Details', socRows)}
                    {renderTableSectionWeb(' 10.Land', landRows)}
                    {renderTableSectionWeb(' 11.New Building Accommodation', buildingRows)}

                    {/* Accommodation Table */}
                    {accomidation.length > 0 && (
                        <>
                            <h5 className="mt-4">Accommodation required & provided for existing PG programmes</h5>
                            <table className="table table-bordered">
                                <thead className="table-primary">
                                    <tr><th>Sl.No</th><th>Type of the room</th><th>Rooms Required</th><th>Area Required (sft)</th><th>Rooms Provided</th><th>Sufficient as per norms</th><th>Remarks</th></tr>
                                </thead>
                                <tbody>
                                    {accomidation.map((item, idx) => (
                                        <tr key={idx}>
                                            <td>{idx + 1}</td>
                                            <td>{item.particular_name}</td>
                                            <td>{item.no_of_rooms}</td>
                                            <td>{item.total_area}</td>
                                            <td>{item.no_of_rooms}</td>
                                            <td>{item.sufficient_as_per_norms}</td>
                                            <td>{item.remarks}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </>
                    )}

                    {/* UG Programmes */}
                    {ugCourses.length > 0 && (
                        <>
                            <h5 className="mt-4">Details of the UG Programmes</h5>
                            <table className="table table-bordered">
                                <thead className="table-primary"><tr><th>Sl.No</th><th>Type of the UG Programme</th><th>Total No. of programmes</th><th>Sanctioned Intake</th></tr></thead>
                                <tbody>
                                    {ugCourses.map((c, i) => (<tr key={i}><td>{i + 1}</td><td>{c.course_cat_name}</td><td className="text-center">{c.count}</td><td className="text-center">{c.sum}</td></tr>))}
                                </tbody>
                            </table>
                        </>
                    )}

                    {/* PG Programmes */}
                    {pgCourses.length > 0 && (
                        <>
                            <h5 className="mt-4">Details of the PG Programmes</h5>
                            <table className="table table-bordered">
                                <thead className="table-primary"><tr><th>Sl.No</th><th>Type of the PG Programme</th><th>Total No. of programmes</th><th>Sanctioned Intake</th></tr></thead>
                                <tbody>
                                    {pgCourses.map((c, i) => (<tr key={i}><td>{i + 1}</td><td>{c.course_cat_name}</td><td className="text-center">{c.count}</td><td className="text-center">{c.sum}</td></tr>))}
                                </tbody>
                            </table>
                        </>
                    )}

                    {renderTableSectionWeb(' 12.Parking Area', parkingRows)}
                    {renderTableSectionWeb(' 13.Playground & Fire Safety', playgroundRows)}
                    {renderTableSectionWeb('16. Corpus Fund', corpusRows)}
                    {renderTableSectionWeb('17. Infrastructure & Academic Facilities Survey', infraRows)}

                    {/* Final Remarks & Status */}
                    <div className="mt-4">
                        <h5>Final Remarks</h5>
                        <p>{formData.final_remarks || '—'}</p>
                        <h5>Final Status</h5>
                        <p className={formData.final_status === 'DeficiencyIdentified' ? 'text-danger fw-bold' : 'text-success fw-bold'}>
                            {formatValue(formData.final_status)}
                        </p>
                    </div>

                    {/* Deficiencies */}
                    {formData.final_status === 'DeficiencyIdentified' && def.length > 0 && (
                        <>
                            <h5 className="mt-4 text-danger">Deficiencies Identified</h5>
                            <table className="table table-bordered">
                                <thead className="table-danger"><tr><th>S.No</th><th>Deficiency Remarks</th></tr></thead>
                                <tbody>
                                    {def.map((item, idx) => (<tr key={idx}><td>{idx + 1}</td><td>{item.remarks}</td></tr>))}
                                </tbody>
                            </table>
                        </>
                    )}

                    {/* Footer with audit info */}
                    <div className="mt-4 pt-3 border-top text-muted text-center small">
                        <div>College Code: {formData.coll_code || collegeData.coll_code || ''}</div>
                 
                    </div>
                </div>
            </div>
        </div>
    );
};

// =============== MAIN EXPORT ===============
export const CollegeReport = ({ apiData, apiData2, rowData, getOverallData, getSurvey, newAddData }) => {
    const isDataReady = apiData?.data?.length > 0 && apiData2?.length > 0;
    const defList = getOverallData?.def || [];
    const acc = getOverallData?.AfflAccomData || [];
    const data = getOverallData.data || {};
    const shiftAddr = {
        new_mandal: newAddData?.new_mandal || '',
        new_district: newAddData?.new_district || '',
        new_pincode: newAddData?.new_pincode || '',
        address_ofnew_location: newAddData?.address_ofnew_location || '',
        mandal_name: newAddData?.new_mandal || newAddData?.mandal_name || '',
        district_name: newAddData?.new_district || newAddData?.district_name || '',
    };
    return (
        <div>
            <div className="card-footer bg-light text-end">
                <PDFDownloadLink
                    key={JSON.stringify(apiData) + JSON.stringify(apiData2) + defList.length + acc.length}
                    document={<PDFReport data={apiData} data2={apiData2} rowData={rowData} defData={defList} acc={acc} getSurvey={getSurvey} shiftAddr={shiftAddr} />}
                    fileName="shifting_scrutiny_report.pdf"
                    style={{ background: '#2e7d32', color: 'white', border: 'none', padding: '12px 24px', borderRadius: 40, cursor: 'pointer', fontSize: 16, fontWeight: 'bold', textDecoration: 'none' }}
                >
                    {({ loading }) => (loading ? 'Generating PDF...' : '📄 Download PDF Report')}
                </PDFDownloadLink>
            </div>
            <WebViewReport data={apiData} data2={apiData2} rowData={rowData} defData={defList} acc={acc} mainData={data} getSurvey={getSurvey} shiftAddr={shiftAddr} />
        </div>
    );
};